import env from '@/constants/env'
import type {
  Bottomline,
  YotpoProductBottomlineOperation,
  YotpoProductsBottomlinesOperation,
} from '@/types/yotpo'

export const client = async <T>(
  path: string,
  headers?: HeadersInit,
): Promise<{ status: number; body: T } | never> => {
  try {
    const response = await fetch(`https://api.yotpo.com/${path}`, {
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
        ...headers,
      },
    })
    const body = await response.json()

    if (!response.ok) {
      return Promise.reject(
        new Error(
          `Failed to fetch from Yotpo (/${path}): ${JSON.stringify(body)}`,
        ),
      )
    }

    return {
      status: response.status,
      body,
    }
  } catch (error) {
    if (typeof error === 'string') {
      throw new Error(error)
    }
    return Promise.reject(error)
  }
}

export const getProductBottomline = async (
  productId: string,
): Promise<Bottomline> => {
  if (!productId) {
    throw new Error('getProductBottomline: productId is required')
  }

  if (!env.PUBLIC_YOTPO_APP_KEY) {
    throw new Error('getProductBottomline: PUBLIC_YOTPO_APP_KEY is required')
  }

  const path = `products/${env.PUBLIC_YOTPO_APP_KEY}/${productId}/bottomline`
  const res = await client<YotpoProductBottomlineOperation>(path)
  const { bottomline } = res.body.response

  return bottomline
}

const getProductsBottomlines = async ({
  count,
  page,
}: {
  count: number
  page: number
}) => {
  const path = `v1/apps/${env.PUBLIC_YOTPO_APP_KEY}/bottom_lines?count=${count}&page=${page}`
  const res = await client<YotpoProductsBottomlinesOperation>(path)
  return res.body.response
}

export const getAllProductBottomlines = async () => {
  if (!env.PUBLIC_YOTPO_APP_KEY) {
    throw new Error(
      'getAllProductBottomLines: PUBLIC_YOTPO_APP_KEY is required',
    )
  }

  const count = 100
  const bottomlines = []
  let page = 1
  let response = await getProductsBottomlines({ count, page })

  while (response.bottomlines.length > 0) {
    bottomlines.push(...response.bottomlines)
    page++
    response = await getProductsBottomlines({ count, page })
  }

  return bottomlines
}
